import { economyFetch as fetch } from './economyClient';
/**
 * MagiSp 認証フック
 *
 * Supabase Auth を使用した認証管理
 * - 匿名ユーザー自動作成
 * - セッション管理
 * - プロフィール取得・更新
 * - 匿名→正規アカウント連携
 */

import { useState, useEffect, useCallback, createContext, useContext, ReactNode } from 'react';
import { Session, User as AuthUser, AuthError } from '@supabase/supabase-js';
import type { User } from './types/database';

// ============================================
// 型定義
// ============================================

/** サーバーから取得したプレイヤーデータ */
export interface ServerPlayerData {
  snapshot?: import('./economyClient').EconomySnapshot;
  gold: number;
  unopenedPacks: number;
  unopenedPacksByType: Record<string, number>;
  collection: Array<{ card_id: string; rarity: string; quantity: number }>;
  decks?: unknown;
  stats?: {
    totalBattles?: number;
    wins?: number;
    losses?: number;
    packsOpened?: number;
    totalGoldEarned?: number;
    totalGoldSpent?: number;
  };
}

export interface AuthState {
  /** Supabase Auth セッション */
  session: Session | null;
  /** Supabase Auth ユーザー */
  authUser: AuthUser | null;
  /** MagiSp ユーザープロフィール */
  profile: User | null;
  /** サーバーから取得したプレイヤーデータ */
  serverPlayerData: ServerPlayerData | null;
  /** 読み込み中フラグ */
  loading: boolean;
  /** エラー */
  error: AuthError | Error | null;
  /** 認証済みかどうか */
  isAuthenticated: boolean;
  /** 匿名ユーザーかどうか */
  isAnonymous: boolean;
}

export interface UseAuthReturn extends AuthState {
  /** 匿名サインイン（自動実行） */
  signInAnonymously: () => Promise<void>;
  /** メールでサインアップ */
  signUpWithEmail: (email: string, password: string, displayName?: string) => Promise<void>;
  /** メールでサインイン */
  signInWithEmail: (email: string, password: string) => Promise<void>;
  /** 匿名アカウントを正規アカウントに連携 */
  linkAccount: (email: string, password: string, displayName?: string) => Promise<void>;
  /** 表示名を更新 */
  updateDisplayName: (displayName: string) => Promise<void>;
  /** サインアウト */
  signOut: () => Promise<void>;
  /** プロフィールを再取得 */
  refreshProfile: () => Promise<void>;
  /** サーバーのプレイヤーデータを再取得 */
  refreshServerPlayerData: () => Promise<void>;
  /** ログイン時刻を更新 */
  updateLastLogin: () => Promise<void>;
}

// ============================================
// 定数
// ============================================

/** 匿名サインインのリトライ回数 */
const MAX_RETRY_COUNT = 3;
/** リトライ間隔（ms） */
const RETRY_DELAY = 1000;
/** Supabase URL */
const SUPABASE_URL = 'https://vikqkqwklbdjfhkydzpw.supabase.co';
/** Supabase Anon Key */
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZpa3FrcXdrbGJkamZoa3lkenB3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjYwNjYzODcsImV4cCI6MjA4MTY0MjM4N30.PPIf98mu1HgR95CaymSJiV9E5hnlsMJuQeXw2MNqKsw';
/** localStorage のセッションキー */
const STORAGE_KEY = 'sb-vikqkqwklbdjfhkydzpw-auth-token';
/** トークン期限切れまでの余裕（5分前にリフレッシュ） */
const TOKEN_EXPIRY_MARGIN_MS = 5 * 60 * 1000;
/** トークン確認間隔（10分ごと） */
const TOKEN_CHECK_INTERVAL_MS = 10 * 60 * 1000;

// ============================================
// ヘルパー関数
// ============================================

/**
 * JWTトークンが期限切れかどうかを確認
 * @param token JWTトークン
 * @returns 期限切れの場合true
 */
function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiryTime = payload.exp * 1000; // Unix timestamp → ms
    return Date.now() > expiryTime - TOKEN_EXPIRY_MARGIN_MS;
  } catch {
    // パースに失敗した場合は期限切れとみなす
    return true;
  }
}

/**
 * トークンをリフレッシュ
 * @param refreshToken リフレッシュトークン
 * @returns 新しいセッションデータ、または失敗時null
 */
async function refreshAccessToken(refreshToken: string): Promise<{
  access_token: string;
  refresh_token: string;
  user: AuthUser;
} | null> {
  try {
    console.log('[refreshAccessToken] Refreshing token...');
    const response = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      console.error('[refreshAccessToken] Failed:', response.status);
      return null;
    }

    const data = await response.json();
    console.log('[refreshAccessToken] Success!');
    return {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      user: data.user,
    };
  } catch (err) {
    console.error('[refreshAccessToken] Error:', err);
    return null;
  }
}

// ============================================
// useAuth フック
// ============================================

// モジュールレベルの初期化フラグ
// React Strict ModeではuseEffect内のクリーンアップが間に実行されるため、
// useRefだとリセットされてしまう。モジュールレベル変数なら
// アンマウント・再マウントの間も値が保持される。
let isInitializing = false;

/**
 * 認証ロジック本体（内部用）
 * AuthProvider内でのみ使用される
 */
function useAuthInternal(): UseAuthReturn {
  const [session, setSession] = useState<Session | null>(null);
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [profile, setProfile] = useState<User | null>(null);
  const [serverPlayerData, setServerPlayerData] = useState<ServerPlayerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AuthError | Error | null>(null);

  // ============================================
  // プロフィール取得
  // ============================================

  const fetchProfile = useCallback(async (userId: string): Promise<User | null> => {
    try {
      // REST APIで直接取得（JSクライアントはハングするため）
      const stored = localStorage.getItem(STORAGE_KEY);
      const accessToken = stored ? JSON.parse(stored)?.access_token : null;

      if (!accessToken) {
        return null;
      }

      const response = await fetch(
        `${SUPABASE_URL}/rest/v1/users?id=eq.${userId}&select=*`,
        {
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        return null;
      }

      const data = await response.json();

      if (data && data.length > 0) {
        return data[0] as User;
      }

      return null;
    } catch (err) {
      console.error('[fetchProfile] Error:', err);
      return null;
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!authUser) return;
    const newProfile = await fetchProfile(authUser.id);
    setProfile(newProfile);
  }, [authUser, fetchProfile]);

  // ============================================
  // プレイヤーデータ取得（サーバー）
  // ============================================

  const fetchPlayerData = useCallback(async (accessToken: string): Promise<ServerPlayerData | null> => {
    try {
      console.log('[fetchPlayerData] Fetching player data from server...');

      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/collection`,
        {
          method: 'GET',
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        console.error('[fetchPlayerData] HTTP error:', response.status);
        return null;
      }

      const data = await response.json();

      if (!data?.success) {
        console.error('[fetchPlayerData] API error:', data?.error);
        return null;
      }

      console.log('[fetchPlayerData] Success:', { gold: data.gold, collectionSize: data.collection?.length });

      return {
        snapshot: data.player,
        gold: data.gold ?? 0,
        unopenedPacks: data.unopened_packs ?? 0,
        unopenedPacksByType: data.unopened_packs_by_type ?? {},
        collection: data.collection ?? [],
        decks: data.decks,
        stats: data.stats,
      };
    } catch (err) {
      console.error('[fetchPlayerData] Error:', err);
      return null;
    }
  }, []);

  const refreshServerPlayerData = useCallback(async () => {
    const stored = localStorage.getItem(STORAGE_KEY);
    const accessToken = stored ? JSON.parse(stored)?.access_token : null;

    if (!accessToken) return;

    const data = await fetchPlayerData(accessToken);
    setServerPlayerData(data);
  }, [fetchPlayerData]);

  // ============================================
  // 匿名サインイン
  // ============================================

  const signInAnonymously = useCallback(async () => {
    console.log('[signInAnonymously] Starting...');
    setLoading(true);
    setError(null);

    for (let attempt = 0; attempt < MAX_RETRY_COUNT; attempt++) {
      try {
        console.log(`[signInAnonymously] Attempt ${attempt + 1}...`);

        // REST APIで匿名サインイン（JSクライアントはハングするため）
        const response = await fetch(`${SUPABASE_URL}/auth/v1/signup`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            // メールなしで登録すると匿名ユーザーになる
            // Supabaseの設定で「Allow anonymous sign-ins」が有効である必要がある
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error_description || data.msg || '匿名サインインに失敗しました');
        }

        if (data.access_token && data.user) {
          console.log('[signInAnonymously] Success! User ID:', data.user.id);

          // localStorageにセッションを保存
          const sessionData = {
            access_token: data.access_token,
            refresh_token: data.refresh_token,
            user: data.user,
          };
          localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionData));
          console.log('[signInAnonymously] Session saved to localStorage');

          setSession(sessionData as unknown as Session);
          setAuthUser(data.user);

          // プロフィール取得（トリガーで作成されるまで少し待つ）
          await new Promise(resolve => setTimeout(resolve, 500));
          const userProfile = await fetchProfile(data.user.id);
          console.log('[signInAnonymously] Profile:', userProfile);
          setProfile(userProfile);

          // プレイヤーデータ取得（サーバーから）
          const playerData = await fetchPlayerData(data.access_token);
          console.log('[signInAnonymously] PlayerData:', { gold: playerData?.gold });
          setServerPlayerData(playerData);
        }

        setLoading(false);
        return;
      } catch (err) {
        console.error(`[signInAnonymously] Attempt ${attempt + 1} failed:`, err);
        if (attempt < MAX_RETRY_COUNT - 1) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (attempt + 1)));
        } else {
          setError(err as AuthError);
          setLoading(false);
        }
      }
    }
  }, [fetchProfile, fetchPlayerData]);

  // ============================================
  // メールサインアップ
  // ============================================

  const signUpWithEmail = useCallback(async (
    email: string,
    password: string,
    displayName?: string
  ) => {
    setLoading(true);
    setError(null);

    try {
      // REST APIで新規登録（JSクライアントはハングするため）
      const response = await fetch(`${SUPABASE_URL}/auth/v1/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          email,
          password,
          data: {
            display_name: displayName || null,
            is_anonymous: false,
          },
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error_description || data.msg || '登録に失敗しました');
      }

      // localStorageにセッションを保存
      if (data.access_token && data.user) {
        const sessionData = {
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          user: data.user,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionData));

        // リロードして新しいセッションを読み込む
        window.location.reload();
      }
    } catch (err) {
      setError(err as AuthError);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ============================================
  // メールサインイン
  // ============================================

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    setLoading(true);
    setError(null);

    try {
      // REST APIでログイン（JSクライアントはハングするため）
      const response = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          email,
          password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error_description || data.msg || 'ログインに失敗しました');
      }

      // localStorageにセッションを保存
      if (data.access_token && data.user) {
        const sessionData = {
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          user: data.user,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionData));

        // public.users テーブルをUPSERT（プロフィールが存在することを確認）
        try {
          await fetch(
            `${SUPABASE_URL}/rest/v1/users?on_conflict=id`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'apikey': SUPABASE_ANON_KEY,
                'Authorization': `Bearer ${data.access_token}`,
                'Prefer': 'resolution=merge-duplicates',
              },
              body: JSON.stringify({
                id: data.user.id,
                is_anonymous: false,
                display_name: data.user.user_metadata?.display_name || null,
              }),
            }
          );
        } catch (e) {
          console.warn('[signInWithEmail] users table upsert error:', e);
        }

        // リロードして新しいセッションを読み込む
        window.location.reload();
      }
    } catch (err) {
      setError(err as AuthError);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ============================================
  // 匿名アカウント連携
  // ============================================

  const linkAccount = useCallback(async (
    email: string,
    password: string,
    displayName?: string
  ) => {
    // セッションがない場合はエラー
    if (!session) {
      throw new Error('セッションがありません。ページを再読み込みしてください。');
    }

    // 既にメールが登録されている場合はエラー（authUser.email で判定）
    if (authUser?.email) {
      throw new Error('既に正規アカウントに連携済みです。');
    }

    setLoading(true);
    setError(null);

    try {
      // REST APIで認証ユーザーを更新（JSクライアントはハングするため）
      const response = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          email,
          password,
          data: {
            display_name: displayName || profile?.display_name || null,
            is_anonymous: false,
          },
        }),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.error_description || responseData.msg || 'アカウント連携に失敗しました');
      }

      // localStorageのセッションデータを更新（REST APIは自動更新しないため）
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          parsed.user = {
            ...parsed.user,
            email: responseData.email,
            is_anonymous: false,
            user_metadata: {
              ...parsed.user?.user_metadata,
              display_name: displayName || profile?.display_name || null,
              is_anonymous: false,
            },
          };
          localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
        }
      } catch (e) {
        console.warn('[linkAccount] localStorage update error:', e);
      }

      // アカウント連携後、新しい認証情報でログインして新しいトークンを取得
      // （古いトークンが無効になる可能性があるため）
      console.log('[linkAccount] Re-authenticating with new credentials...');
      const loginResponse = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          email,
          password,
        }),
      });

      const loginData = await loginResponse.json();

      if (!loginResponse.ok) {
        console.error('[linkAccount] Re-authentication failed:', loginData);
        throw new Error('アカウント連携は成功しましたが、再ログインに失敗しました。手動でログインしてください。');
      }

      // 新しいトークンでlocalStorageを更新
      if (loginData.access_token && loginData.user) {
        const newSessionData = {
          access_token: loginData.access_token,
          refresh_token: loginData.refresh_token,
          user: loginData.user,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newSessionData));

        // public.users テーブルをUPSERT（新しいトークンを使用）
        try {
          await fetch(
            `${SUPABASE_URL}/rest/v1/users?on_conflict=id`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'apikey': SUPABASE_ANON_KEY,
                'Authorization': `Bearer ${loginData.access_token}`,
                'Prefer': 'resolution=merge-duplicates',
              },
              body: JSON.stringify({
                id: loginData.user.id,
                is_anonymous: false,
                display_name: displayName || profile?.display_name || null,
              }),
            }
          );
        } catch (e) {
          console.warn('[linkAccount] users table upsert error:', e);
        }
      }

      // セッションを更新するためリロード
      window.location.reload();
    } catch (err) {
      console.error('[linkAccount] Error:', err);
      setError(err as AuthError);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [session, authUser, profile, refreshProfile]);

  // ============================================
  // 表示名更新
  // ============================================

  const updateDisplayName = useCallback(async (displayName: string) => {
    if (!authUser) {
      throw new Error('Not authenticated');
    }

    setError(null);

    try {
      // REST APIで表示名を更新（JSクライアントはハングするため）
      const stored = localStorage.getItem(STORAGE_KEY);
      const accessToken = stored ? JSON.parse(stored)?.access_token : null;

      if (!accessToken) {
        throw new Error('No access token');
      }

      const response = await fetch(
        `${SUPABASE_URL}/rest/v1/users?id=eq.${authUser.id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${accessToken}`,
            'Prefer': 'return=minimal',
          },
          body: JSON.stringify({ display_name: displayName }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || '表示名の更新に失敗しました');
      }

      await refreshProfile();
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  }, [authUser, refreshProfile]);

  // ============================================
  // ログイン時刻更新
  // ============================================

  const updateLastLogin = useCallback(async () => {
    if (!authUser) return;

    try {
      // REST APIでRPC関数を呼び出す（JSクライアントはハングするため）
      const stored = localStorage.getItem(STORAGE_KEY);
      const accessToken = stored ? JSON.parse(stored)?.access_token : null;

      if (!accessToken) return;

      await fetch(`${SUPABASE_URL}/rest/v1/rpc/update_last_login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({}),
      });
    } catch (err) {
      console.error('Failed to update last login:', err);
    }
  }, [authUser]);

  // ============================================
  // サインアウト
  // ============================================

  const signOut = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // localStorageからセッションを削除（JSクライアントはハングするため）
      localStorage.removeItem(STORAGE_KEY);

      // 状態をクリア
      setSession(null);
      setAuthUser(null);
      setProfile(null);

      // リロードして新しい匿名セッションを開始
      window.location.reload();
    } catch (err) {
      setError(err as AuthError);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ============================================
  // 初期化 & セッション監視
  // ============================================

  useEffect(() => {
    let mounted = true;
    let tokenCheckInterval: NodeJS.Timeout | null = null;

    // localStorageから直接セッションを読み取る（JSクライアントのgetSession()はハングするため）
    // トークンが期限切れの場合は自動でリフレッシュ
    const checkStoredSession = async (): Promise<boolean> => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);

        if (stored) {
          const parsed = JSON.parse(stored);

          if (parsed?.access_token && parsed?.user) {
            // トークンが期限切れかチェック
            if (isTokenExpired(parsed.access_token)) {
              console.log('[useAuth] Token expired, attempting refresh...');

              if (parsed.refresh_token) {
                const newSession = await refreshAccessToken(parsed.refresh_token);

                if (newSession) {
                  // 新しいセッションを保存
                  localStorage.setItem(STORAGE_KEY, JSON.stringify(newSession));

                  if (mounted) {
                    setSession(newSession as unknown as Session);
                    setAuthUser(newSession.user);
                    setLoading(false);

                    // プロフィール取得
                    const userProfile = await fetchProfile(newSession.user.id);
                    if (mounted) {
                      setProfile(userProfile);
                    }

                    // プレイヤーデータ取得（mountedチェック不要 - React 18対応）
                    const playerData = await fetchPlayerData(newSession.access_token);
                    setServerPlayerData(playerData);
                  }
                  return true;
                } else {
                  // リフレッシュ失敗 → セッションクリア
                  console.log('[useAuth] Token refresh failed, clearing session');
                  localStorage.removeItem(STORAGE_KEY);
                  return false;
                }
              } else {
                // refresh_tokenがない → セッションクリア
                console.log('[useAuth] No refresh token, clearing session');
                localStorage.removeItem(STORAGE_KEY);
                return false;
              }
            }

            // トークンが有効な場合
            if (mounted) {
              setSession(parsed as Session);
              setAuthUser(parsed.user);
              setLoading(false);

              // プロフィール取得
              const userProfile = await fetchProfile(parsed.user.id);
              if (mounted) {
                setProfile(userProfile);
              }

              // プレイヤーデータ取得
              // 注意: mountedチェックを削除 - React 18のStrict Modeでは
              // 初回の非同期処理完了時にmounted=falseになるため
              // React 18はアンマウント後の状態更新を適切に処理する
              const playerData = await fetchPlayerData(parsed.access_token);
              setServerPlayerData(playerData);
            }
            return true;
          }
        }
      } catch (e) {
        console.warn('[useAuth] Error reading stored session:', e);
      }
      return false;
    };

    // 定期的にトークンをチェックしてリフレッシュ
    const startTokenCheckInterval = () => {
      tokenCheckInterval = setInterval(async () => {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed?.access_token && isTokenExpired(parsed.access_token) && parsed?.refresh_token) {
            console.log('[useAuth] Periodic check: Token expired, refreshing...');
            const newSession = await refreshAccessToken(parsed.refresh_token);
            if (newSession) {
              localStorage.setItem(STORAGE_KEY, JSON.stringify(newSession));
              if (mounted) {
                setSession(newSession as unknown as Session);
                setAuthUser(newSession.user);
              }
            }
          }
        }
      }, TOKEN_CHECK_INTERVAL_MS);
    };

    // 初期化
    // 注意: onAuthStateChangeは削除済み
    // このアプリはlocalStorageで独自にセッションを管理し、
    // Supabase JSクライアントの状態管理は使用しない（ハング問題の回避）
    const initialize = async () => {
      // React Strict Mode二重実行防止
      // モジュールレベル変数を使用（クリーンアップでリセットされない）
      if (isInitializing) {
        console.log('[useAuth] Initialization already in progress, skipping...');
        return;
      }
      isInitializing = true;
      console.log('[useAuth] Initialization started');

      try {
        // まずlocalStorageからセッションを復元（必要に応じてリフレッシュ）
        const hasStoredSession = await checkStoredSession();

        if (!hasStoredSession) {
          // セッションがなければ匿名サインイン
          try {
            await signInAnonymously();
          } catch (err) {
            if (mounted) {
              console.error('[useAuth] Auth initialization error:', err);
              setError(err as Error);
              setLoading(false);
            }
          }
        }

        // 定期的なトークンチェックを開始
        startTokenCheckInterval();
      } finally {
        // 初期化完了（成功・失敗問わず）
        console.log('[useAuth] Initialization completed');
        // フラグはリセットしない - 同一セッション中は再初期化を防ぐ
        // HMR時はモジュール再読み込みでリセットされる
      }
    };

    initialize();

    return () => {
      mounted = false;
      if (tokenCheckInterval) {
        clearInterval(tokenCheckInterval);
      }
      // 注意: isInitializingはリセットしない
      // React Strict Modeではクリーンアップが間に実行されるため、
      // ここでリセットすると二重実行を防げない
    };
  }, [fetchProfile, fetchPlayerData, signInAnonymously]);

  // ============================================
  // 戻り値
  // ============================================

  // デバッグログ: 毎回のレンダリングで状態を確認
  console.log('[useAuth] Current state:', {
    hasSession: !!session,
    sessionAccessToken: session?.access_token?.substring(0, 20) + '...',
    authUserId: authUser?.id,
    authUserEmail: authUser?.email,
    profileId: profile?.id,
    serverPlayerDataGold: serverPlayerData?.gold,
    loading,
    // isAnonymous: authUserにemailがあれば確実に非匿名
    // DBのprofile.is_anonymousが古い場合のフォールバック
    isAnonymous: authUser?.email ? false : (profile?.is_anonymous ?? true),
  });

  return {
    // State
    session,
    authUser,
    profile,
    serverPlayerData,
    loading,
    error,
    isAuthenticated: !!session,
    // isAnonymous: authUserにemailがあれば確実に非匿名
    isAnonymous: authUser?.email ? false : (profile?.is_anonymous ?? true),

    // Actions
    signInAnonymously,
    signUpWithEmail,
    signInWithEmail,
    linkAccount,
    updateDisplayName,
    signOut,
    refreshProfile,
    refreshServerPlayerData,
    updateLastLogin,
  };
}

// ============================================
// AuthContext & AuthProvider
// ============================================

/**
 * 認証コンテキスト
 * アプリ全体で認証状態を共有するために使用
 */
const AuthContext = createContext<UseAuthReturn | null>(null);

/**
 * 認証プロバイダー
 * アプリのルートレベルでラップして使用
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const auth = useAuthInternal();
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

/**
 * 認証フック（パブリック）
 * コンポーネントから認証状態にアクセスするために使用
 *
 * 注意: AuthProviderでラップされていない場合はエラーになります
 */
export function useAuth(): UseAuthReturn {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// ============================================
// デフォルトエクスポート
// ============================================

export default useAuth;
